import { useTranslation } from 'react-i18next';
import type { DimensionScore } from '@/api/types';
import { fmtScore } from '@/utils/analytics';
import { DimensionLabel } from '../../components/DimensionLabel';

const TOP_COLORS = ['var(--cp-success)', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'];
const BOT_COLORS = ['var(--cp-danger-light)', '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'];

interface RankedDimensionListProps {
  dimensions: DimensionScore[];
  rankColors: string[];
  scoreColor: string;
  emptyLabel: string;
  evaluationsLabel: (count: number) => string;
}

/** Lista numerada de dimensiones ordenadas por puntaje; compartida por los dos bloques. */
function RankedDimensionList({
  dimensions,
  rankColors,
  scoreColor,
  emptyLabel,
  evaluationsLabel,
}: RankedDimensionListProps) {
  if (dimensions.length === 0) {
    return <div className="au-tb-empty">{emptyLabel}</div>;
  }

  return (
    <div className="au-tb-list">
      {dimensions.map((dimension, index) => (
        <div key={dimension.id} className="au-tb-item">
          <div className="au-tb-rank" style={{ background: rankColors[index] }}>
            {index + 1}
          </div>
          <div className="au-tb-info">
            <DimensionLabel
              name={dimension.name}
              isBehavior={dimension.isBehavior}
              fundamentalName={dimension.fundamentalName}
              color={dimension.color}
            />
            <span className="au-tb-meta">{evaluationsLabel(dimension.evaluations)}</span>
          </div>
          <span className="au-tb-score" style={{ color: scoreColor }}>
            {fmtScore(dimension.score)}
          </span>
        </div>
      ))}
    </div>
  );
}

interface TopBotBehaviorsProps {
  topBehaviors: DimensionScore[];
  bottomBehaviors: DimensionScore[];
}

export function TopBotBehaviors({ topBehaviors, bottomBehaviors }: TopBotBehaviorsProps) {
  const { t } = useTranslation();
  const evaluationsLabel = (count: number) => t('analytics.summary.evaluationsCount', { count });

  return (
    <div className="au-topbot">
      <div className="au-topbot-card">
        <div className="au-topbot-title" style={{ color: 'var(--cp-success)' }}>
          <i className="fas fa-arrow-trend-up" aria-hidden="true" />
          {t('analytics.summary.topBehaviors')}
        </div>
        <div className="au-tb-subtitle">{t('analytics.summary.topDailyHabitsSubtitle')}</div>
        <RankedDimensionList
          dimensions={topBehaviors}
          rankColors={TOP_COLORS}
          scoreColor="var(--cp-success)"
          emptyLabel={t('analytics.noData')}
          evaluationsLabel={evaluationsLabel}
        />
      </div>

      <div className="au-topbot-card">
        <div className="au-topbot-title" style={{ color: 'var(--cp-danger-light)' }}>
          <i className="fas fa-arrow-trend-down" aria-hidden="true" />
          {t('analytics.summary.opportunities')}
        </div>
        <div className="au-tb-subtitle">{t('analytics.summary.oppSubtitle')}</div>
        <RankedDimensionList
          dimensions={bottomBehaviors}
          rankColors={BOT_COLORS}
          scoreColor="var(--cp-danger-light)"
          emptyLabel={t('analytics.noData')}
          evaluationsLabel={evaluationsLabel}
        />
      </div>
    </div>
  );
}
